/* eslint-disable no-unused-vars */
import React, { forwardRef, useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../analyticaldashboard.module.css";
import DropdownSelect from "./DropdownSelect";
import ElevenLabsBalanceHeader from "./ElevenLabsBalanceHeader";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ─── Trigger button ───────────────────────────────────────────────────────────
const CustomDateInput = forwardRef(({ displayLabel, onClick, onClear }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className="btn btn-light bg-white border border-secondary-soft text-dark-75 font-weight-bold px-4 py-2 d-flex flex-column align-items-start justify-content-center rounded-lg shadow-sm w-100"
    style={{ minHeight: "55px", height: "100%", width: "100%", minWidth: 0, border: "1px solid #E4E6EF", whiteSpace: "normal" }}
  >
    <span style={{ fontSize: "0.75rem", color: "#5E6278", fontWeight: 600, marginBottom: 2 }}>Date Range</span>
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
        <svg className="mr-3" style={{ flexShrink: 0 }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <span style={{ color: "#181C32", fontWeight: 600, fontSize: "0.9rem", wordBreak: "break-word", textAlign: "left" }}>{displayLabel}</span>
      </div>
      {displayLabel !== "All Date" ? (
        <span
          onClick={(e) => { e.stopPropagation(); onClear && onClear(); }}
          style={{ padding: 4, borderRadius: "50%", backgroundColor: "#f3f6f9", cursor: "pointer", marginLeft: 8, lineHeight: 1 }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#A1A5B7" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </span>
      ) : (
        <svg className="ml-3 text-muted" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="17 11 12 6 7 11"></polyline>
          <polyline points="17 13 12 18 7 13"></polyline>
        </svg>
      )}
    </div>
  </button>
));

// ─── Main component ───────────────────────────────────────────────────────────
const AnalyticsFilters = ({
  agent,
  setAgent,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedUser,
  setSelectedUser,
  selectedBatch,
  setSelectedBatch,
  selectedIndustry,
  setSelectedIndustry,
  userOptions = [],
  batchOptions = [],
  industryOptions = [],
  showUserDropdown = true
}) => {
  const datePickerRef = useRef(null);
  const dropdownRef = useRef(null);

  const userDropdownRef = useRef(null);
  const batchDropdownRef = useRef(null);
  const industryDropdownRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [batchDropdownOpen, setBatchDropdownOpen] = useState(false);
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState("days"); // "days"|"months"|"years"
  const [yearGridStart, setYearGridStart] = useState(new Date().getFullYear() - 5);

  // Close agent dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
      if (userDropdownRef.current && !userDropdownRef.current.contains(e.target)) setUserDropdownOpen(false);
      if (batchDropdownRef.current && !batchDropdownRef.current.contains(e.target)) setBatchDropdownOpen(false);
      if (industryDropdownRef.current && !industryDropdownRef.current.contains(e.target)) setIndustryDropdownOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Reset calendar mode when it closes
  const handleCalendarClose = () => setCalendarViewMode("days");

  // ── Range selection logic ──────────────────────────────────────────────────
  const handleDateChange = (date) => {
    if (!date) return;
    if (!startDate || (startDate && endDate)) {
      // First click → set start, keep open
      setStartDate(date);
      setEndDate(null);
      setCalendarViewMode("days");
    } else {
      // Second click → set end
      if (date < startDate) {
        setStartDate(date);
      } else {
        setEndDate(date);
        if (datePickerRef.current) datePickerRef.current.setOpen(false);
      }
    }
  };

  // ── Display label ──────────────────────────────────────────────────────────
  const getDateDisplayLabel = () => {
    const opt = { month: "short", day: "numeric", year: "numeric" };
    if (startDate && endDate) return `${startDate.toLocaleDateString("en-US", opt)} – ${endDate.toLocaleDateString("en-US", opt)}`;
    if (startDate) return `${startDate.toLocaleDateString("en-US", opt)} – ...`;
    return "All Date";
  };

  // ── Agent options ──────────────────────────────────────────────────────────
  const agentOptions = [
    // { value: "all",           label: "All Agents" },
    { value: "inquery", label: "Inquiry (agent - 1)" },
    { value: "oldleads", label: "Old Leads (agent - 2)" },
    { value: "incomingleads", label: "Incoming Leads (agent - 3)" },
    { value: "newleads", label: "New Leads (agent - 4)" },
    // { value: "oldleads_newleads", label: "Old & New Leads (agent - 5)" },
  ];
  const selectedAgentOption = agentOptions.find((o) => o.value === agent) || agentOptions[0];

  // ── Inline styles shared ───────────────────────────────────────────────────
  const overlayStyle = {
    position: "absolute",
    top: 0, left: 0, right: 0,
    background: "#fff",
    zIndex: 1200,
    borderRadius: "12px",
    padding: "18px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
    minHeight: "255px",
  };

  const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px",
    marginTop: "12px",
  };

  const gridItemStyle = (isActive, disabled) => ({
    padding: "10px 4px",
    borderRadius: "8px",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    fontWeight: 600,
    fontSize: "0.92rem",
    background: isActive ? "#6f42c1" : "#F5F5FA",
    color: isActive ? "#fff" : disabled ? "#ccc" : "#3F4254",
    transition: "all 0.15s",
    opacity: disabled ? 0.5 : 1,
  });

  const navBtnStyle = {
    width: 32, height: 32, borderRadius: 6,
    border: "1px solid #E4E6EF", background: "#fff",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  };

  // ── renderCustomHeader ─────────────────────────────────────────────────────
  const renderCustomHeader = ({
    date, changeYear, changeMonth, decreaseMonth, increaseMonth,
    prevMonthButtonDisabled, nextMonthButtonDisabled,
  }) => {
    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();
    const today = new Date();
    const endYear = today.getFullYear();

    return (
      <div style={{ position: "relative", width: "100%" }}>

        {/* ── Month Grid Overlay ── */}
        {calendarViewMode === "months" && (
          <div style={overlayStyle}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F0F0F5", paddingBottom: 8 }}>
              <button style={navBtnStyle} type="button" onClick={() => changeYear(currentYear - 1)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <span
                style={{ fontWeight: 700, fontSize: "1.05rem", color: "#181C32", cursor: "pointer", padding: "2px 10px", borderRadius: 6 }}
                onClick={() => { setYearGridStart(currentYear - 5); setCalendarViewMode("years"); }}
              >
                {currentYear}
              </span>
              <button style={navBtnStyle} type="button" onClick={() => changeYear(currentYear + 1)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
            <div style={gridStyle}>
              {SHORT_MONTHS.map((m, idx) => {
                const isFuture = currentYear === endYear && idx > today.getMonth();
                const isActive = currentMonth === idx;
                return (
                  <button
                    key={m} type="button"
                    disabled={isFuture}
                    style={gridItemStyle(isActive, isFuture)}
                    onClick={() => { changeMonth(idx); setCalendarViewMode("days"); }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Year Grid Overlay ── */}
        {calendarViewMode === "years" && (
          <div style={overlayStyle}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #F0F0F5", paddingBottom: 8 }}>
              <button style={navBtnStyle} type="button" onClick={() => setYearGridStart((y) => y - 12)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
              </button>
              <span style={{ fontWeight: 700, fontSize: "1.02rem", color: "#181C32" }}>
                {yearGridStart} – {yearGridStart + 11}
              </span>
              <button style={navBtnStyle} type="button" onClick={() => setYearGridStart((y) => y + 12)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
              </button>
            </div>
            <div style={gridStyle}>
              {Array.from({ length: 12 }, (_, i) => yearGridStart + i).map((yr) => {
                const isFuture = yr > endYear;
                const isActive = currentYear === yr;
                return (
                  <button
                    key={yr} type="button"
                    disabled={isFuture}
                    style={gridItemStyle(isActive, isFuture)}
                    onClick={() => { changeYear(yr); setCalendarViewMode("months"); }}
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Day-view header (always visible underneath overlays) ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px" }}>
          <button
            type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}
            style={{ ...navBtnStyle, opacity: prevMonthButtonDisabled ? 0.35 : 1, cursor: prevMonthButtonDisabled ? "not-allowed" : "pointer" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span
              style={{ fontWeight: 700, fontSize: "1.05rem", color: "#181C32", cursor: "pointer", padding: "3px 8px", borderRadius: 6, transition: "background 0.15s" }}
              onClick={() => setCalendarViewMode("months")}
              onMouseEnter={(e) => { e.target.style.background = "#F3F0FD"; e.target.style.color = "#6f42c1"; }}
              onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#181C32"; }}
            >
              {MONTH_NAMES[currentMonth]}
            </span>
            <span
              style={{ fontWeight: 700, fontSize: "1.05rem", color: "#181C32", cursor: "pointer", padding: "3px 8px", borderRadius: 6, transition: "background 0.15s" }}
              onClick={() => { setYearGridStart(currentYear - 5); setCalendarViewMode("years"); }}
              onMouseEnter={(e) => { e.target.style.background = "#F3F0FD"; e.target.style.color = "#6f42c1"; }}
              onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#181C32"; }}
            >
              {currentYear}
            </span>
          </div>

          <button
            type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}
            style={{ ...navBtnStyle, opacity: nextMonthButtonDisabled ? 0.35 : 1, cursor: nextMonthButtonDisabled ? "not-allowed" : "pointer" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#181C32" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>

        {/* Status hint when in end-date mode */}
        {startDate && !endDate && (
          <div style={{ background: "#6f42c1", color: "#fff", fontSize: "0.72rem", fontWeight: 600, textAlign: "center", padding: "3px 0", letterSpacing: "0.03em" }}>
            Start selected — now pick an end date
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="card card-custom gutter-b">
      <style>{`
        .analytics-date-picker-container .react-datepicker-wrapper,
        .analytics-date-picker-container .react-datepicker__input-container {
          display: block !important;
          width: 100% !important;
        }
      `}</style>
      {/* Card Header */}
      <div className="card-header d-flex align-items-center justify-content-between py-5" style={{ borderBottom: "1px solid #EFF2F5", minHeight: "auto" }}>
        <div className="card-title align-items-start flex-column m-0">
          <div className="d-flex align-items-center">
            {/* Funnel Icon */}
            <div
              className="d-flex align-items-center justify-content-center mr-3"
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                backgroundColor: "#F3F0FD",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6f42c1"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
              </svg>
            </div>
            <h3 className="card-label font-weight-bolder text-dark mb-0 d-flex align-items-center" style={{ fontSize: "1.1rem" }}>
              Filters
              <span className="text-muted font-size-sm font-weight-bold ml-3" style={{ fontSize: "0.82rem", textTransform: "none", color: "#A1A5B7" }}>
                filter data based on your selected criteria
              </span>
            </h3>
          </div>
        </div>
        <div className="card-toolbar">
          <ElevenLabsBalanceHeader />
        </div>
      </div>

      {/* Card Body */}
      <div className="card-body py-6 px-7">
        <div className="analytics-filter-container">
          {/* ── Date Range Picker ── */}
          <div className="analytics-filter-item analytics-date-picker-container">
            <DatePicker
              ref={datePickerRef}
              wrapperClassName="w-100"
              selected={startDate && !endDate ? null : startDate}
              onChange={handleDateChange}
              maxDate={new Date()}
              customInput={
                <CustomDateInput
                  displayLabel={getDateDisplayLabel()}
                  onClear={() => {
                    setStartDate(null);
                    setEndDate(null);
                  }}
                />
              }
              shouldCloseOnSelect={false}
              showDaysOutsideCurrentMonth
              selectsStart={!startDate || !!(startDate && endDate)}
              selectsEnd={!!(startDate && !endDate)}
              startDate={startDate}
              endDate={endDate}
              onCalendarClose={handleCalendarClose}
              popperClassName="custom-datepicker-popper"
              calendarClassName="custom-datepicker-calendar"
              popperPlacement="bottom-start"
              popperProps={{ strategy: "fixed" }}
              renderCustomHeader={renderCustomHeader}
            />
          </div>

          {/* ── Agent Dropdown ── */}
          <div className="analytics-filter-item">
            <DropdownSelect
              label="Agent"
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                </svg>
              }
              selectedValue={agent}
              options={agentOptions}
              onChange={setAgent}
              isOpen={dropdownOpen}
              onToggle={() => setDropdownOpen(!dropdownOpen)}
              dropdownRef={dropdownRef}
              minWidth="100%"
              style={{ width: "100%" }}
            />
          </div>

          {/* ── User Dropdown ── */}
          {(agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads") && showUserDropdown && (
            <div className="analytics-filter-item">
              <DropdownSelect
                label="User"
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                }
                selectedValue={selectedUser}
                options={userOptions}
                onChange={setSelectedUser}
                isOpen={userDropdownOpen}
                onToggle={() => setUserDropdownOpen(!userDropdownOpen)}
                dropdownRef={userDropdownRef}
                minWidth="100%"
                style={{ width: "100%" }}
                placeholder="All Users"
              />
            </div>
          )}

          {/* ── Batch Name Dropdown ── */}
          {(agent === "oldleads" || agent === "newleads" || agent === "oldleads_newleads") && (
            <div className="analytics-filter-item">
              <DropdownSelect
                label="Batch Name"
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="9"></rect>
                    <rect x="14" y="3" width="7" height="5"></rect>
                    <rect x="14" y="12" width="7" height="9"></rect>
                    <rect x="3" y="16" width="7" height="5"></rect>
                  </svg>
                }
                selectedValue={selectedBatch}
                options={batchOptions}
                onChange={setSelectedBatch}
                isOpen={batchDropdownOpen}
                onToggle={() => setBatchDropdownOpen(!batchDropdownOpen)}
                dropdownRef={batchDropdownRef}
                minWidth="100%"
                style={{ width: "100%" }}
                placeholder="All Batches"
              />
            </div>
          )}

          {/* ── Industry Dropdown ── */}
          {agent === "newleads" && (
            <div className="analytics-filter-item">
              <DropdownSelect
                label="Industry"
                icon={
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                }
                selectedValue={selectedIndustry}
                options={industryOptions}
                onChange={setSelectedIndustry}
                isOpen={industryDropdownOpen}
                onToggle={() => setIndustryDropdownOpen(!industryDropdownOpen)}
                dropdownRef={industryDropdownRef}
                minWidth="100%"
                style={{ width: "100%" }}
                placeholder="All Industries"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsFilters;
